/**
 * THE WALLET RECORD — an email, a sub-organisation id and an address, in `localStorage`.
 *
 * IT CONFERS NOTHING. An address you cannot sign for is not access. It exists so a returning visitor's top bar
 * paints their address before the first network call, and so re-signing-in does not have to re-create an
 * account. THIS FILE IS THE ONE READER AND WRITER of `localStorage["kinetiq.v3.wallet"]`; `stores/wallet.ts`
 * holds the in-memory copy and says so in its own comment, because two files persisting one record is the
 * drift that makes sign-out clear the copy nobody is reading.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * ACCOUNT CREATION IS BUILT (2026-08-14) — AND IT DELIBERATELY DOES NOT LIVE HERE
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────
 * The onboarding sequence — email → OTP → sub-organisation → signing session — is `auth/onboard.ts` against
 * the four routes `/auth/otp/start`, `/auth/otp/verify`, `/auth/wallet`, `/auth/otp/login`, which the trader
 * surface now serves. This file stays what it always was: the RECORD's one reader and writer. `onboard.ts`
 * calls `rememberWallet` at the end of a successful flow and that is the whole of the relationship.
 *
 * The separation is the point. Persistence has no idea how an account came to exist, so a change to the
 * vendor flow cannot alter what a stored record means — and the record is read on EVERY boot, by a path that
 * must keep working for users provisioned before this flow existed.
 *
 * ⚠️ THE RECORD STILL CONFERS NOTHING, and that is what makes the ordering in `onboard.ts` load-bearing: it
 * calls `storeUserSession` BEFORE `rememberWallet`, so a record on disk always implies a signing session was
 * stored. A record written first would render a user as having a wallet they cannot sign for — which is
 * exactly what an address without a key is.
 */

import { setKnownWallet } from "../stores/session.ts";
import { type WalletRecord, setWallet } from "../stores/wallet.ts";

const RECORD_KEY = "kinetiq.v3.wallet";

export function loadWalletRecord(): WalletRecord | null {
  try {
    const raw = globalThis.localStorage?.getItem(RECORD_KEY);
    if (!raw) return null;
    const w = JSON.parse(raw) as WalletRecord;
    // A record without BOTH fields cannot produce a signature, so it is worth nothing and is treated as absent
    // rather than half-restored — a half-restored record renders a signed-out user as signed in.
    return w?.subOrgId && w?.address ? w : null;
  } catch {
    return null; // a corrupt entry is signed out, which is recoverable; surfacing it is not useful
  }
}

function persist(record: WalletRecord | null): void {
  try {
    if (record) globalThis.localStorage?.setItem(RECORD_KEY, JSON.stringify(record));
    else globalThis.localStorage?.removeItem(RECORD_KEY);
  } catch {
    /* storage refused (private browsing); the in-memory store still serves this tab */
  }
}

export function rememberWallet(record: WalletRecord): void {
  persist(record);
  setWallet(record);
  setKnownWallet(record.address, record.subOrgId);
}

export function forgetWallet(): void {
  persist(null);
  setWallet(null);
}

/** Restore before the first paint, so a returning tab shows its masked address instead of flashing "Sign in"
 *  and correcting. The correction that DOES happen — `GET /session` a moment later — is one transition, and
 *  every button that spends money is gated on the VERIFIED state, never on this. */
export function restoreWallet(): WalletRecord | null {
  const record = loadWalletRecord();
  if (record) {
    setWallet(record);
    setKnownWallet(record.address, record.subOrgId);
  }
  return record;
}
